import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Typography,
  InputAdornment,
  IconButton
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  ArrowDropDown,
  SettingsApplications,
  LaptopWindows
} from '@mui/icons-material';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Points, PointMaterial, Stars } from '@react-three/drei';
import * as THREE from 'three';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import avatarBase from '../images/avatar_base.png';
import avatarBoca from '../images/avatar_boca.png';
import avatarBigoteIzq from '../images/avatar_bigote_izquierdo.png';
import avatarBigoteDer from '../images/avatar_bigote_derecho.png';
import avatarCejaIzq from '../images/avatar_ceja_izquierda.png';
import avatarCejaDer from '../images/avatar_ceja_derecha.png';
import avatarOjoIzq from '../images/avatar_ojo_izquierdo.png';
import avatarOjoDer from '../images/avatar_ojo_derecho.png';
import avatarOjoCerradoIzq from '../images/avatar_ojo_cerrado_izquierdo.png';
import avatarOjoCerradoDer from '../images/avatar_ojo_cerrado_derecho.png';
import avatarManoIzq from '../images/avatar_mano_izq.png';
import avatarManoDer from '../images/avatar_mano_der.png';
import avatarBocaFeliz from '../images/avatar_boca_feliz.png';
import avatarFelizCejaIzq from '../images/avatar_feliz_ceja_izq.png';
import avatarFelizCejaDer from '../images/avatar_feliz_ceja_der.png';
import avatarFelizOjoIzq from '../images/avatar_feliz_ojo_izq.png';
import avatarFelizOjoDer from '../images/avatar_feliz_ojo_der.png';
import avatarManoLike from '../images/avatar_mano_like.png';
import avatarBocaBad from '../images/avatar_bad_boca.png';
import avatarBadCejaIzq from '../images/avatar_bad_ceja_izq.png';
import avatarBadCejaDer from '../images/avatar_bad_ceja_der.png';
import avatarBadOjoIzq from '../images/avatar_bad_ojo_izq.png';
import avatarBadOjoDer from '../images/avatar_bad_ojo_der.png';
import avatarManoIndice from '../images/avatar_mano_indice.png';

// Campo de estrellas con efecto gravitacional
const StarField = ({ mousePosition, blackHolePosition }) => {
  const ref = useRef();
  const originalPositions = useRef();

  const [positions] = useState(() => {
    const positions = new Float32Array(4000 * 3);
    const colors = new Float32Array(4000 * 3);

    for (let i = 0; i < 4000; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 2000;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 2000;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 2000;

      // Colores variados para las estrellas
      const colorChoice = Math.random();
      if (colorChoice < 0.7) {
        colors[i * 3] = 1; colors[i * 3 + 1] = 1; colors[i * 3 + 2] = 1; // Blanco
      } else if (colorChoice < 0.85) {
        colors[i * 3] = 1; colors[i * 3 + 1] = 0.8; colors[i * 3 + 2] = 0.6; // Amarillo
      } else {
        colors[i * 3] = 0.8; colors[i * 3 + 1] = 0.9; colors[i * 3 + 2] = 1; // Azul
      }
    }

    // Guardar posiciones originales
    originalPositions.current = new Float32Array(positions);

    return { positions, colors };
  });

  useFrame((state) => {
    if (ref.current) {
      // Movimiento basico de rotacion
      ref.current.rotation.x += 0.0002;
      ref.current.rotation.y += 0.0003;
      ref.current.rotation.z += 0.0001;

      // Efecto gravitacional del agujero negro
      if (blackHolePosition && ref.current.geometry && originalPositions.current) {
        const positionsArr = ref.current.geometry.attributes.position.array;
        const originals = originalPositions.current;

        for (let i = 0; i < positionsArr.length; i += 3) {
          // Restaurar posicion original
          positionsArr[i] = originals[i];
          positionsArr[i + 1] = originals[i + 1];
          positionsArr[i + 2] = originals[i + 2];

          const dx = positionsArr[i] - blackHolePosition.x;
          const dy = positionsArr[i + 1] - blackHolePosition.y;
          const dz = positionsArr[i + 2] - blackHolePosition.z;
          const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

          // Efecto gravitacional mas fuerte
          if (distance < 150) {
            const force = 200 / (distance * distance + 1);
            const bendFactor = Math.min(force * 0.01, 0.8);

            positionsArr[i] -= dx * bendFactor;
            positionsArr[i + 1] -= dy * bendFactor;
            positionsArr[i + 2] -= dz * bendFactor;
          }
        }
        ref.current.geometry.attributes.position.needsUpdate = true;
      }
    }
  });

  return (
    <Points ref={ref} positions={positions.positions} stride={3} frustumCulled={false}>
      <PointMaterial
        transparent
        vertexColors
        size={2}
        sizeAttenuation={true}
        depthWrite={false}
      />
      <bufferAttribute
        attach="geometry-attributes-color"
        array={positions.colors}
        count={positions.colors.length / 3}
        itemSize={3}
      />
    </Points>
  );
};

// Constelacion ENIGMA
const EnigmaConstellation = () => {
  const starsRef = useRef();
  const linesRef = useRef();
  const noiseRef = useRef();
  const groupRef = useRef();
  const floatPhase = useRef(Math.random() * Math.PI * 2);
  const baseRotationZ = 0.383972; // ~ 22 grados a la derecha
  const startTimeRef = useRef(null);
  const [animationComplete, setAnimationComplete] = useState(false);
  const noiseTargets = useRef([]);
  const layoutRef = useRef({ x: -230, y: 120, z: -100, scale: 0.75 });
  const [, setLayoutTick] = useState(0);

  // Coordenadas para ENIGMA, posicion base arriba/izquierda
  const [enigmaPoints] = useState(() => {
    const xOffset = -90;
    const yOffset = 150;

    return [
      // E (trazos alineados)
      { x: xOffset - 110, y: yOffset + 40, z: -400, letter: 'E', order: 0, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset - 110, y: yOffset + 10, z: -398, letter: 'E', order: 1, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset - 110, y: yOffset - 10, z: -398, letter: 'E', order: 2, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset - 110, y: yOffset - 40, z: -400, letter: 'E', order: 3, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset - 60, y: yOffset + 40, z: -405, letter: 'E', order: 4, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset - 60, y: yOffset + 0, z: -402, letter: 'E', order: 5, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset - 60, y: yOffset - 40, z: -405, letter: 'E', order: 6, size: Math.random() * 1.5 + 1, colorType: Math.random() },

      // N (verticales definidas y diagonal clara)
      { x: xOffset - 20, y: yOffset + 40, z: -400, letter: 'N', order: 7, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset - 20, y: yOffset + 10, z: -398, letter: 'N', order: 8, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset - 20, y: yOffset - 10, z: -398, letter: 'N', order: 9, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset - 20, y: yOffset - 40, z: -400, letter: 'N', order: 10, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 5, y: yOffset + 15, z: -398, letter: 'N', order: 11, size: Math.random() * 1.5 + 1, colorType: Math.random() }, // punto diagonal
      { x: xOffset + 30, y: yOffset + 40, z: -405, letter: 'N', order: 12, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 30, y: yOffset + 5, z: -402, letter: 'N', order: 13, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 30, y: yOffset - 40, z: -405, letter: 'N', order: 14, size: Math.random() * 1.5 + 1, colorType: Math.random() },

      // I
      { x: xOffset + 60, y: yOffset + 40, z: -400, letter: 'I', order: 15, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 58, y: yOffset + 10, z: -395, letter: 'I', order: 16, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 60, y: yOffset, z: -400, letter: 'I', order: 17, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 62, y: yOffset - 20, z: -398, letter: 'I', order: 18, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 60, y: yOffset - 40, z: -400, letter: 'I', order: 19, size: Math.random() * 1.5 + 1, colorType: Math.random() },

      // G
      { x: xOffset + 110, y: yOffset + 40, z: -400, letter: 'G', order: 20, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 112, y: yOffset + 15, z: -395, letter: 'G', order: 21, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 110, y: yOffset, z: -400, letter: 'G', order: 22, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 110, y: yOffset - 40, z: -400, letter: 'G', order: 23, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 135, y: yOffset + 35, z: -398, letter: 'G', order: 24, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 160, y: yOffset + 40, z: -405, letter: 'G', order: 25, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 160, y: yOffset - 40, z: -405, letter: 'G', order: 26, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 140, y: yOffset - 5, z: -398, letter: 'G', order: 27, size: Math.random() * 1.5 + 1, colorType: Math.random() },

      // M
      { x: xOffset + 190, y: yOffset + 40, z: -400, letter: 'M', order: 28, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 192, y: yOffset + 15, z: -395, letter: 'M', order: 29, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 190, y: yOffset, z: -400, letter: 'M', order: 30, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 190, y: yOffset - 40, z: -400, letter: 'M', order: 31, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 215, y: yOffset + 25, z: -398, letter: 'M', order: 32, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 240, y: yOffset + 40, z: -405, letter: 'M', order: 33, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 238, y: yOffset + 10, z: -400, letter: 'M', order: 34, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 240, y: yOffset, z: -405, letter: 'M', order: 35, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 240, y: yOffset - 40, z: -405, letter: 'M', order: 36, size: Math.random() * 1.5 + 1, colorType: Math.random() },

      // A
      { x: xOffset + 270, y: yOffset + 40, z: -400, letter: 'A', order: 37, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 268, y: yOffset + 15, z: -395, letter: 'A', order: 38, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 270, y: yOffset, z: -400, letter: 'A', order: 39, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 270, y: yOffset - 40, z: -400, letter: 'A', order: 40, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 295, y: yOffset + 35, z: -398, letter: 'A', order: 41, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 320, y: yOffset + 40, z: -405, letter: 'A', order: 42, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 318, y: yOffset + 10, z: -400, letter: 'A', order: 43, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 320, y: yOffset, z: -405, letter: 'A', order: 44, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 320, y: yOffset - 40, z: -405, letter: 'A', order: 45, size: Math.random() * 1.5 + 1, colorType: Math.random() },
      { x: xOffset + 295, y: yOffset - 5, z: -398, letter: 'A', order: 46, size: Math.random() * 1.5 + 1, colorType: Math.random() }
    ];
  });
  const noiseStars = useMemo(() => {
    return Array.from({ length: 40 }).map(() => ({
      x: -60 + Math.random() * 360,
      y: 80 + Math.random() * 120,
      z: -390 + Math.random() * 30,
      size: Math.random() * 1.2 + 0.6,
      colorType: Math.random()
    }));
  }, []);
  useEffect(() => {
    if (!noiseTargets.current.length) {
      noiseTargets.current = noiseStars.map(() => ({
        x: (Math.random() - 0.5) * 2000,
        y: (Math.random() - 0.5) * 2000,
        z: (Math.random() - 0.5) * 2000
      }));
    }
  }, [noiseStars]);

  useEffect(() => {
    const updateLayout = () => {
      const isMobile = window.innerWidth <= 768;
      layoutRef.current = isMobile
        ? { x: 0, y: 170, z: -220, scale: 0.55 }
        : { x: -230, y: 120, z: -100, scale: 0.75 };
      setLayoutTick((t) => t + 1);
    };
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  const [connections] = useState(() => [
    [0, 1], [1, 2], [2, 3], [0, 4], [2, 5], [3, 6],
    [7, 8], [8, 9], [9, 10], [12, 13], [13, 14], [7, 11], [11, 14],
    [15, 16], [16, 17], [17, 18], [18, 19],
    [20, 21], [21, 22], [22, 23], [20, 24], [24, 25], [23, 26], [22, 27], [27, 26],
    [28, 29], [29, 30], [30, 31], [28, 32], [32, 33], [33, 34], [34, 35], [35, 36],
    [37, 38], [38, 39], [39, 40], [37, 41], [41, 42], [42, 43], [43, 44], [44, 45], [39, 46], [46, 44]
  ]);
  const letterOrder = useMemo(() => ['E', 'N', 'I', 'G', 'M', 'A'], []);
  const lineLetterIndex = useMemo(
    () => connections.map(([a]) => letterOrder.indexOf(enigmaPoints[a].letter)),
    [connections, enigmaPoints, letterOrder]
  );
  const letterConnectionOrder = useMemo(() => {
    return letterOrder.map((letter) =>
      connections
        .map((conn, idx) => ({ idx, letterIdx: letterOrder.indexOf(enigmaPoints[conn[0]].letter) }))
        .filter(({ letterIdx }) => letterIdx === letterOrder.indexOf(letter))
        .map(({ idx }) => idx)
    );
  }, [connections, enigmaPoints, letterOrder]);

  useFrame((state) => {
    const time = state.clock.elapsedTime;
    if (startTimeRef.current === null) startTimeRef.current = time;
    const elapsed = time - startTimeRef.current;
    const letterDuration = 0.425; // velocidad x2
    const letterGap = 0.1;
    const totalDuration = letterOrder.length * letterDuration + (letterOrder.length - 1) * letterGap;
    const clampedElapsed = Math.min(elapsed, totalDuration);
    const letterProgress = letterOrder.map((_, idx) => {
      const start = idx * (letterDuration + letterGap);
      const local = (clampedElapsed - start) / letterDuration;
      if (local <= 0) return 0;
      if (local >= 1) return 1;
      return local;
    });
    if (!animationComplete && elapsed >= totalDuration) {
      setAnimationComplete(true);
    }

    if (starsRef.current && linesRef.current) {
      starsRef.current.children.forEach((star, index) => {
        const letterIdx = letterOrder.indexOf(enigmaPoints[index].letter);
        const progress = animationComplete ? 1 : (letterProgress[letterIdx] ?? 0);
        star.visible = true;
        const material = star.material;
        const twinkle = Math.sin(time * 3 + index * 0.6) * 0.05 + 1;
        const activeBoost = 0.15 * progress;
        material.opacity = Math.min(1, 0.95 + 0.05 * twinkle + activeBoost);
      });

      linesRef.current.children.forEach((line, index) => {
        const letterIdx = lineLetterIndex[index];
        const letterProg = animationComplete ? 1 : (letterProgress[letterIdx] ?? 0);
        const seq = letterConnectionOrder[letterIdx] || [];
        const seqIdx = seq.indexOf(index);
        const perConnection = seq.length ? 1 / seq.length : 1;
        const connectionProg = seqIdx >= 0 ? Math.min(Math.max((letterProg - perConnection * seqIdx) / perConnection, 0), 1) : letterProg;
        const startPoint = enigmaPoints[connections[index][0]];
        const endPoint = enigmaPoints[connections[index][1]];

        if (connectionProg > 0) {
          line.visible = true;
          const positions = line.geometry.attributes.position.array;
          positions[0] = startPoint.x;
          positions[1] = startPoint.y;
          positions[2] = startPoint.z;

          const lerpFactor = connectionProg;
          positions[3] = startPoint.x + (endPoint.x - startPoint.x) * lerpFactor;
          positions[4] = startPoint.y + (endPoint.y - startPoint.y) * lerpFactor;
          positions[5] = startPoint.z + (endPoint.z - startPoint.z) * lerpFactor;
          line.geometry.attributes.position.needsUpdate = true;
          line.material.opacity = animationComplete ? 0.9 : 0.25 + 0.55 * connectionProg;
        } else {
          line.visible = false;
        }
      });
    }

    if (noiseRef.current) {
      noiseRef.current.children.forEach((star, idx) => {
        const material = star.material;
        const twinkle = Math.sin(time * 2.5 + idx * 0.9) * 0.08 + 1;
        material.opacity = Math.min(1, 0.9 + 0.05 * twinkle);

        if (animationComplete && noiseTargets.current[idx]) {
          const target = noiseTargets.current[idx];
          star.position.x += (target.x - star.position.x) * 0.015;
          star.position.y += (target.y - star.position.y) * 0.015;
          star.position.z += (target.z - star.position.z) * 0.015;
          material.opacity = Math.min(1, 0.6 + 0.05 * twinkle);
        }
      });
    }

    if (groupRef.current) {
      const driftX = Math.sin(time * 0.15 + floatPhase.current) * 12;
      const driftY = Math.cos(time * 0.12 + floatPhase.current) * 10;
      const { x, y, z, scale } = layoutRef.current;
      groupRef.current.position.set(x + driftX, y + driftY, z);
      groupRef.current.scale.set(scale, scale, scale);
      groupRef.current.rotation.x = Math.sin(time * 0.08) * 0.08;
      groupRef.current.rotation.y = Math.cos(time * 0.07) * 0.08;
      groupRef.current.rotation.z = baseRotationZ;
    }
  });

  return (
    <group ref={groupRef} scale={0.75}>
      <group ref={starsRef}>
        {enigmaPoints.map((point, index) => {
          let color;
          if (point.colorType < 0.7) {
            color = '#ffffff';
          } else if (point.colorType < 0.85) {
            color = '#fff8dc';
          } else {
            color = '#e6f3ff';
          }

          return (
            <mesh key={index} position={[point.x, point.y, point.z]} visible={true}>
              <sphereGeometry args={[point.size, 8, 8]} />
              <meshBasicMaterial
                color={color}
                transparent
                opacity={0.1}
              />
            </mesh>
          );
        })}
      </group>

      <group ref={noiseRef}>
        {noiseStars.map((point, index) => {
          let color;
          if (point.colorType < 0.7) {
            color = '#ffffff';
          } else if (point.colorType < 0.85) {
            color = '#fff8dc';
          } else {
            color = '#e6f3ff';
          }

          return (
            <mesh key={`noise-${index}`} position={[point.x, point.y, point.z]} visible={true}>
              <sphereGeometry args={[point.size, 8, 8]} />
              <meshBasicMaterial
                color={color}
                transparent
                opacity={0.85}
              />
            </mesh>
          );
        })}
      </group>

      <group ref={linesRef}>
        {connections.map((connection, index) => {
          const start = enigmaPoints[connection[0]];
          const end = enigmaPoints[connection[1]];

          return (
            <line key={`line-${index}`} visible={false}>
              <bufferGeometry>
                <bufferAttribute
                  attach="attributes-position"
                  array={new Float32Array([
                    start.x, start.y, start.z,
                    end.x, end.y, end.z
                  ])}
                  count={2}
                  itemSize={3}
                />
              </bufferGeometry>
              <lineBasicMaterial
                color="#ffffff"
                transparent
                opacity={0.8}
                linewidth={1}
              />
            </line>
          );
        })}
      </group>
    </group>
  );
};

// Agujero negro que se mueve libremente con gravitacion visual
const BlackHole = ({ setPosition }) => {
  const ref = useRef();
  const [position, setInternalPosition] = useState([200, -100, -300]);

  useFrame((state) => {
    if (ref.current) {
      const time = state.clock.elapsedTime * 0.1;
      const newX = Math.sin(time) * 100 + 100;
      const newY = Math.cos(time * 0.7) * 80 - 50;
      const newZ = Math.sin(time * 0.5) * 50 - 250;

      ref.current.position.set(newX, newY, newZ);

      const newPos = { x: newX, y: newY, z: newZ };
      setInternalPosition([newX, newY, newZ]);
      setPosition(newPos);
    }
  });

  return (
    <group>
      <mesh ref={ref} position={position}>
        <sphereGeometry args={[12, 32, 32]} />
        <meshBasicMaterial color="#000000" />
      </mesh>

      <mesh ref={ref} position={position}>
        <sphereGeometry args={[25, 32, 32]} />
        <meshBasicMaterial
          color="#000000"
          transparent
          opacity={0.3}
          side={THREE.FrontSide}
        />
      </mesh>

      <mesh ref={ref} position={position}>
        <sphereGeometry args={[40, 32, 32]} />
        <meshBasicMaterial
          color="#1a1a1a"
          transparent
          opacity={0.1}
          side={THREE.FrontSide}
        />
      </mesh>
    </group>
  );
};

// Componente principal del fondo espacial limpio
const SpaceBackground = ({ mousePosition }) => {
  const [blackHolePosition, setBlackHolePosition] = useState({ x: 200, y: -100, z: -300 });

  return (
    <>
      <color attach="background" args={['#000510']} />
      <fog attach="fog" args={['#000510', 100, 1200]} />
      <ambientLight intensity={0.1} />
      <pointLight position={[100, 100, 100]} intensity={0.3} color="#ffffff" />

      <StarField mousePosition={mousePosition} blackHolePosition={blackHolePosition} />
      <BlackHole setPosition={setBlackHolePosition} />
      <EnigmaConstellation />
      <Stars
        radius={500}
        depth={100}
        count={3000}
        factor={4}
        saturation={0}
        fade
        speed={0.3}
      />
    </>
  );
};

// Control suave para navegar con scroll en profundidad
const ScrollNavigator = () => {
  const { camera } = useThree();
  const targetZ = useRef(camera.position.z);
  const targetX = useRef(camera.position.x);
  const targetY = useRef(camera.position.y);
  const dragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const onWheel = (e) => {
      e.preventDefault();
      targetZ.current = Math.min(400, Math.max(-200, targetZ.current + e.deltaY * 0.25));
      targetX.current = Math.min(400, Math.max(-400, targetX.current + e.deltaX * 0.4));
    };

    const onPointerDown = (e) => {
      dragging.current = true;
      lastPos.current = { x: e.clientX, y: e.clientY };
    };

    const onPointerUp = () => {
      dragging.current = false;
    };

    const onPointerMove = (e) => {
      if (!dragging.current) return;
      const dx = e.clientX - lastPos.current.x;
      const dy = e.clientY - lastPos.current.y;
      lastPos.current = { x: e.clientX, y: e.clientY };
      targetX.current = Math.min(400, Math.max(-400, targetX.current - dx * 0.5));
      targetY.current = Math.min(300, Math.max(-300, targetY.current + dy * 0.5));
    };

      window.addEventListener('wheel', onWheel, { passive: false });
      window.addEventListener('pointerdown', onPointerDown);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointerleave', onPointerUp);
      window.addEventListener('pointermove', onPointerMove);
      return () => {
        window.removeEventListener('wheel', onWheel);
        window.removeEventListener('pointerdown', onPointerDown);
        window.removeEventListener('pointerup', onPointerUp);
        window.removeEventListener('pointerleave', onPointerUp);
        window.removeEventListener('pointermove', onPointerMove);
      };
    }, []);

  useFrame(() => {
    camera.position.z += (targetZ.current - camera.position.z) * 0.06;
    camera.position.x += (targetX.current - camera.position.x) * 0.08;
    camera.position.y += (targetY.current - camera.position.y) * 0.08;
    camera.updateProjectionMatrix();
  });

  return null;
};

const AvatarLayered = ({ mousePosition, avatarMood, setAvatarMood }) => {
  const [blink, setBlink] = useState(false);
  const handNoAnim = keyframes`
    0% { transform: translate(-50%, 0) rotate(0deg); }
    20% { transform: translate(-50%, 0) rotate(-50deg); }
    40% { transform: translate(-50%, 0) rotate(50deg); }
    60% { transform: translate(-50%, 0) rotate(-50deg); }
    80% { transform: translate(-50%, 0) rotate(50deg); }
    100% { transform: translate(-50%, 0) rotate(0deg); }
  `;

  useEffect(() => {
    const interval = setInterval(() => {
      setBlink(true);
      setTimeout(() => setBlink(false), 120);
      setTimeout(() => setBlink(true), 220);
      setTimeout(() => setBlink(false), 340);
    }, 3200 + Math.random() * 1800);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey && e.altKey && e.key.toLowerCase() === 'h') {
        setAvatarMood((prev) => (prev === 'happy' ? 'neutral' : 'happy'));
      }
      if (e.ctrlKey && e.altKey && e.key.toLowerCase() === 'b') {
        setAvatarMood((prev) => (prev === 'bad' ? 'neutral' : 'bad'));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setAvatarMood]);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const baseShiftX = clamp((mousePosition?.x || 0) * 10, -12, 12);
  const baseShiftY = clamp((mousePosition?.y || 0) * -8, -12, 12); // invertir para que suba con el mouse
  const isHappy = avatarMood === 'happy';
  const isBad = avatarMood === 'bad';
  const layerSx = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    pointerEvents: 'none'
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        top: { xs: -123, sm: -143 },
        left: '50%',
        transform: 'translateX(-50%)',
        width: { xs: 200, sm: 220 },
        height: 'auto',
        zIndex: 11
      }}
    >
      <Box
        component="img"
        src={avatarBase}
        alt="Avatar base"
        sx={{
          width: '100%',
          height: 'auto',
          display: 'block',
          pointerEvents: 'none'
        }}
      />
      <Box component="img" src={avatarManoIzq} alt="Avatar mano izquierda" sx={layerSx} />
      {isHappy && <Box component="img" src={avatarManoLike} alt="Avatar mano derecha like" sx={layerSx} />}
      {!isHappy && !isBad && <Box component="img" src={avatarManoDer} alt="Avatar mano derecha" sx={layerSx} />}
      {isBad && (
        <Box
          component="img"
          src={avatarManoIndice}
          alt="Avatar mano indice"
          sx={{
            position: 'absolute',
            width: '24%',
            height: 'auto',
            left: '80%',
            bottom: '10%',
            transform: 'translate(-50%, 0)',
            transformOrigin: '50% 100%',
            pointerEvents: 'none',
            animation: `${handNoAnim} 1.6s ease-in-out infinite`
          }}
        />
      )}

      {isHappy && (
        <>
          <Box component="img" src={avatarBocaFeliz} alt="Avatar boca feliz" sx={{ ...layerSx, transform: `translate(${baseShiftX * 0.9}px, ${baseShiftY * 0.6}px)` }} />
          <Box component="img" src={avatarFelizCejaIzq} alt="Avatar ceja feliz izquierda" sx={{ ...layerSx, transform: `translate(${baseShiftX * 0.65}px, ${baseShiftY * 0.7}px)` }} />
          <Box component="img" src={avatarFelizCejaDer} alt="Avatar ceja feliz derecha" sx={{ ...layerSx, transform: `translate(${baseShiftX * 0.65}px, ${baseShiftY * 0.7}px)` }} />
          <Box component="img" src={avatarFelizOjoIzq} alt="Avatar ojo feliz izquierdo" sx={{ ...layerSx, transform: `translate(${baseShiftX * 0.85}px, ${baseShiftY * 0.6}px)` }} />
          <Box component="img" src={avatarFelizOjoDer} alt="Avatar ojo feliz derecho" sx={{ ...layerSx, transform: `translate(${baseShiftX * 0.85}px, ${baseShiftY * 0.6}px)` }} />
        </>
      )}

      {isBad && (
        <>
          <Box component="img" src={avatarBocaBad} alt="Avatar boca bad" sx={{ ...layerSx, transform: `translate(${baseShiftX * 0.9}px, ${baseShiftY * 0.6}px)` }} />
          <Box component="img" src={avatarBadCejaIzq} alt="Avatar ceja bad izquierda" sx={{ ...layerSx, transform: `translate(${baseShiftX * 0.65}px, ${baseShiftY * 0.7}px)` }} />
          <Box component="img" src={avatarBadCejaDer} alt="Avatar ceja bad derecha" sx={{ ...layerSx, transform: `translate(${baseShiftX * 0.65}px, ${baseShiftY * 0.7}px)` }} />
          <Box
            component="img"
            src={avatarBadOjoIzq}
            alt="Avatar ojo bad izquierdo"
            sx={{
              ...layerSx,
              transform: `translate(${baseShiftX * 0.85}px, ${baseShiftY * 0.6}px)`
            }}
          />
          <Box
            component="img"
            src={avatarBadOjoDer}
            alt="Avatar ojo bad derecho"
            sx={{
              ...layerSx,
              transform: `translate(${baseShiftX * 0.85}px, ${baseShiftY * 0.6}px)`
            }}
          />
        </>
      )}

      {!isHappy && !isBad && (
        <>
          <Box component="img" src={avatarBoca} alt="Avatar boca" sx={{ ...layerSx, transform: `translate(${baseShiftX * 0.9}px, ${baseShiftY * 0.6}px)` }} />
          <Box component="img" src={avatarBigoteIzq} alt="Avatar bigote izquierdo" sx={{ ...layerSx, transform: `translate(${baseShiftX * 0.75}px, ${baseShiftY * 0.55}px)` }} />
          <Box component="img" src={avatarBigoteDer} alt="Avatar bigote derecho" sx={{ ...layerSx, transform: `translate(${baseShiftX * 0.75}px, ${baseShiftY * 0.55}px)` }} />
          <Box component="img" src={avatarCejaIzq} alt="Avatar ceja izquierda" sx={{ ...layerSx, transform: `translate(${baseShiftX * 0.65}px, ${baseShiftY * 0.7}px)` }} />
          <Box component="img" src={avatarCejaDer} alt="Avatar ceja derecha" sx={{ ...layerSx, transform: `translate(${baseShiftX * 0.65}px, ${baseShiftY * 0.7}px)` }} />
          {!blink && (
            <>
              <Box component="img" src={avatarOjoIzq} alt="Avatar ojo izquierdo" sx={{ ...layerSx, transform: `translate(${baseShiftX * 0.85}px, ${baseShiftY * 0.6}px)` }} />
              <Box component="img" src={avatarOjoDer} alt="Avatar ojo derecho" sx={{ ...layerSx, transform: `translate(${baseShiftX * 0.85}px, ${baseShiftY * 0.6}px)` }} />
            </>
          )}
          {blink && (
            <>
              <Box component="img" src={avatarOjoCerradoIzq} alt="Avatar ojo cerrado izquierdo" sx={{ ...layerSx, transform: `translate(${baseShiftX * 0.85}px, ${baseShiftY * 0.6}px)` }} />
              <Box component="img" src={avatarOjoCerradoDer} alt="Avatar ojo cerrado derecho" sx={{ ...layerSx, transform: `translate(${baseShiftX * 0.85}px, ${baseShiftY * 0.6}px)` }} />
            </>
          )}
        </>
      )}
    </Box>
  );
};

const compareVersions = (a, b) => {
  const maxLength = Math.max(a.length, b.length);
  for (let i = 0; i < maxLength; i += 1) {
    const aPart = a[i] || 0;
    const bPart = b[i] || 0;
    if (aPart > bPart) return 1;
    if (aPart < bPart) return -1;
  }
  return 0;
};

const parseDownloadCandidates = (indexHtml, subdomain, baseUrl) => {
  const matches = [...indexHtml.matchAll(/href="([^"]+)"/gi)];
  const subdomainLower = subdomain.toLowerCase();
  const portableCandidates = [];
  const installerCandidates = [];

  matches.forEach((match) => {
    const hrefValue = match[1];
    let decodedHref = hrefValue;
    try {
      decodedHref = decodeURIComponent(hrefValue);
    } catch (_) {
      decodedHref = hrefValue;
    }

    const fileName = decodedHref.split('/').pop() || '';
    if (!/\.exe$/i.test(fileName)) return;

    const lowerName = fileName.toLowerCase();
    const withoutExt = lowerName.replace(/\.exe$/i, '');
    const portablePrefix = `${subdomainLower} `;
    const installerPrefix = `${subdomainLower} setup `;
    let type = null;
    let version = '';

    if (withoutExt.startsWith(installerPrefix)) {
      type = 'installer';
      version = withoutExt.slice(installerPrefix.length).trim();
    } else if (withoutExt.startsWith(portablePrefix)) {
      type = 'portable';
      version = withoutExt.slice(portablePrefix.length).trim();
    }

    if (!type || !/^\d+(?:\.\d+)+$/.test(version)) return;

    const versionParts = version.split('.').map(Number);
    const fileData = {
      version,
      versionParts,
      fileName,
      url: new URL(hrefValue, baseUrl).toString()
    };

    if (type === 'installer') {
      installerCandidates.push(fileData);
      return;
    }
    portableCandidates.push(fileData);
  });

  portableCandidates.sort((a, b) => compareVersions(b.versionParts, a.versionParts));
  installerCandidates.sort((a, b) => compareVersions(b.versionParts, a.versionParts));

  return {
    portable: portableCandidates[0] || null,
    installer: installerCandidates[0] || null
  };
};

const SpaceLoginPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [avatarMood, setAvatarMood] = useState('neutral');
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const [isCheckingDownloads, setIsCheckingDownloads] = useState(false);
  const [downloadFiles, setDownloadFiles] = useState({ portable: null, installer: null });
  const { login, error, isLoading, clearError } = useAuth();
  const { showError, showSuccess } = useNotification();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const subdomain = hostname.split('.')[0] || '';
  const updatesBaseUrl = hostname ? `https://${hostname}/updates/` : '';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setAvatarMood('neutral');

    if (error) clearError();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username || !formData.password) {
      showError('Por favor complete todos los campos');
      return;
    }

    const result = await login(formData.username, formData.password, {
      onPreSuccess: () => setAvatarMood('happy')
    });
    if (!result.success) {
      setAvatarMood('bad');
      showError(result.message || 'Error de autenticacion');
    } else {
      setAvatarMood('happy');
      showSuccess('Bienvenido! Inicio de sesion exitoso');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseMove = (e) => {
    const x = (e.clientX / window.innerWidth) * 2 - 1;
    const y = -(e.clientY / window.innerHeight) * 2 + 1;
    setMousePosition({ x, y });
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const checkLatestDownloads = async () => {
    if (!updatesBaseUrl || !subdomain) return;

    setIsCheckingDownloads(true);
    try {
      const response = await fetch(updatesBaseUrl, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`No se pudo leer ${updatesBaseUrl}`);
      }
      const indexHtml = await response.text();
      const latestFiles = parseDownloadCandidates(indexHtml, subdomain, updatesBaseUrl);
      setDownloadFiles(latestFiles);
    } catch {
      setDownloadFiles({ portable: null, installer: null });
    } finally {
      setIsCheckingDownloads(false);
    }
  };

  const handleDownloadMenuOpen = (event) => {
    setDownloadAnchorEl(event.currentTarget);
    if (!isCheckingDownloads) {
      checkLatestDownloads();
    }
  };

  const handleDownloadMenuClose = () => {
    setDownloadAnchorEl(null);
  };

  const handleDownloadSelected = (fileInfo) => {
    if (!fileInfo?.url) return;
    window.location.href = fileInfo.url;
    handleDownloadMenuClose();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          zIndex: 20
        }}
      >
        <Button
          variant="contained"
          size="small"
          onClick={handleDownloadMenuOpen}
          endIcon={<ArrowDropDown />}
          sx={{
            px: 2.5,
            py: 0.75,
            background: 'rgba(234, 77, 255, 0.2)',
            backdropFilter: 'none',
            border: '1px solid rgba(234, 77, 255, 0.4)',
            borderRadius: 2,
            fontWeight: 'bold',
            fontSize: '0.85rem',
            color: 'white',
            textTransform: 'none',
            '&:hover': {
              background: 'rgba(234, 77, 255, 0.3)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 20px rgba(234, 77, 255, 0.3)'
            },
            '& .MuiButton-endIcon': {
              ml: 0.5
            }
          }}
        >
          Descargar
        </Button>
        <Menu
          anchorEl={downloadAnchorEl}
          open={Boolean(downloadAnchorEl)}
          onClose={handleDownloadMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              mt: 0.75,
              minWidth: 250,
              background: 'rgba(24, 8, 30, 0.95)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(234, 77, 255, 0.35)',
              borderRadius: 2,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)'
            }
          }}
          MenuListProps={{
            sx: {
              py: 0.5
            }
          }}
        >
          <MenuItem
            disabled={isCheckingDownloads || !downloadFiles.installer}
            onClick={() => handleDownloadSelected(downloadFiles.installer)}
            sx={{
              color: 'white',
              minHeight: 40,
              '& .MuiListItemIcon-root': {
                minWidth: 32,
                color: 'rgba(234, 77, 255, 0.95)'
              },
              '&.Mui-disabled': {
                opacity: 0.45,
                color: 'rgba(255,255,255,0.8)'
              },
              '&:hover': {
                background: 'rgba(234, 77, 255, 0.16)'
              }
            }}
          >
            <ListItemIcon>
              <SettingsApplications fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={isCheckingDownloads
                ? 'Instalador (Buscando...)'
                : downloadFiles.installer
                  ? `Instalador (${downloadFiles.installer.version})`
                  : 'Instalador (No disponible)'}
            />
          </MenuItem>
          <MenuItem
            disabled={isCheckingDownloads || !downloadFiles.portable}
            onClick={() => handleDownloadSelected(downloadFiles.portable)}
            sx={{
              color: 'white',
              minHeight: 40,
              '& .MuiListItemIcon-root': {
                minWidth: 32,
                color: 'rgba(234, 77, 255, 0.95)'
              },
              '&.Mui-disabled': {
                opacity: 0.45,
                color: 'rgba(255,255,255,0.8)'
              },
              '&:hover': {
                background: 'rgba(234, 77, 255, 0.16)'
              }
            }}
          >
            <ListItemIcon>
              <LaptopWindows fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={isCheckingDownloads
                ? 'Portable (Buscando...)'
                : downloadFiles.portable
                  ? `Portable (${downloadFiles.portable.version})`
                  : 'Portable (No disponible)'}
            />
          </MenuItem>
        </Menu>
      </Box>
      <Canvas
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -1
        }}
        camera={{ position: [0, 0, 50], fov: 75, near: 0.1, far: 1500 }}
      >
        <ScrollNavigator />
        <SpaceBackground mousePosition={mousePosition} />
      </Canvas>

      <Box
        sx={{
          position: 'relative',
      width: '100%',
      maxWidth: 400,
      mx: 2,
      mt: { xs: 6, sm: 8 },
      overflow: 'visible'
    }}
  >
        <AvatarLayered mousePosition={mousePosition} avatarMood={avatarMood} setAvatarMood={setAvatarMood} />
        <Card
          sx={{
            maxWidth: 400,
            width: '100%',
            mx: 'auto',
            background: 'rgba(234, 77, 255, 0.10)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(234, 77, 255, 0.08)',
            borderRadius: 3,
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.10)',
            position: 'relative',
            zIndex: 10,
            pt: 0
          }}
        >
        <CardContent sx={{ p: { xs: 3, sm: 3.5 }, color: 'white' }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: 'white',
                textShadow: '0 2px 8px rgba(234, 77, 255, 0.3)',
                mb: 1
              }}
            >
              ENIGMA PBX
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                letterSpacing: '1px'
              }}
            >
              By MEG IA
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              name="username"
              label="Usuario"
              value={formData.username}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
              autoComplete="off"
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  background: 'rgba(234, 77, 255, 0.04)',
                  backdropFilter: 'none',
                  '& fieldset': {
                    borderColor: 'rgba(234, 77, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(234, 77, 255, 0.35)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgba(234, 77, 255, 0.7)',
                    boxShadow: '0 0 10px rgba(234, 77, 255, 0.15)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&.Mui-focused': {
                    color: 'rgb(234, 77, 255)',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: 'rgba(234, 77, 255, 0.8)' }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              name="password"
              label="Contrasena"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              variant="outlined"
              autoComplete="new-password"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  background: 'rgba(234, 77, 255, 0.04)',
                  backdropFilter: 'none',
                  '& fieldset': {
                    borderColor: 'rgba(234, 77, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(234, 77, 255, 0.35)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgba(234, 77, 255, 0.7)',
                    boxShadow: '0 0 10px rgba(234, 77, 255, 0.15)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&.Mui-focused': {
                    color: 'rgb(234, 77, 255)',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: 'rgba(234, 77, 255, 0.8)' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                      size="small"
                      sx={{ color: 'rgba(234, 77, 255, 0.8)' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
            mt: 2,
            mb: 1.5,
            py: 1.5,
                background: 'rgba(234, 77, 255, 0.2)',
                backdropFilter: 'none',
                border: '1px solid rgba(234, 77, 255, 0.4)',
                borderRadius: 2,
                fontWeight: 'bold',
                fontSize: '1rem',
                color: 'white',
                '&:hover': {
                  background: 'rgba(234, 77, 255, 0.3)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 20px rgba(234, 77, 255, 0.3)'
                },
                '&:disabled': {
                  background: 'rgba(234, 77, 255, 0.15)',
                  color: 'rgba(255, 255, 255, 0.6)'
                }
              }}
            >
              {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Iniciar Sesion'}
            </Button>
          </form>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.75rem'
              }}
            >
              ENIGMA v1.3 — MEG IA. Todos los derechos reservados.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  </Box>
  );
};

export default SpaceLoginPage;
